import { EmptyState } from '../../shared/ui'
import type { AccountsRow } from './useAccounts'

/**
 * Mirrors the legacy table rendering inside `loadAccounts()` at
 * index.html:1754. Same columns, same ICP score color rules, same
 * row-click → legacy `openAccountDrawer(id)` (still owned by index.html).
 */

interface Props {
  rows: AccountsRow[]
  status: 'idle' | 'loading' | 'success' | 'error'
  error: string | null
  /**
   * True when a search or industry filter is set. Zero rows then means
   * "nothing matches"; without filters it means the user has no accounts
   * yet, which gets the onboarding empty state (legacy parity).
   */
  hasFilter?: boolean
}

function openContacts() {
  const tab = document.querySelector('.nav-tab[data-tab="contacts"]') as HTMLElement | null
  tab?.click()
}

export function AccountsTable({ rows, status, error, hasFilter = false }: Props) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Company</th>
            <th>Domain</th>
            <th>Industry</th>
            <th>HQ</th>
            <th>Employees</th>
            <th>Funding</th>
            <th>ICP</th>
            <th>Contacts</th>
          </tr>
        </thead>
        <tbody id="a-tbody">
          {status === 'loading' && (
            <tr className="loading-row">
              <td colSpan={8}>Loading…</td>
            </tr>
          )}
          {status === 'error' && (
            <tr className="loading-row">
              <td colSpan={8} style={{ color: 'var(--red)' }}>
                {error}
              </td>
            </tr>
          )}
          {status === 'success' && rows.length === 0 && !hasFilter && (
            <tr>
              <td colSpan={8} style={{ padding: 0 }}>
                <EmptyState
                  title="No accounts yet."
                  message="Accounts are auto-grouped from your contacts by company. As you add contacts with a company name, accounts populate here."
                  cta={{ label: 'Open People', onClick: openContacts }}
                />
              </td>
            </tr>
          )}
          {status === 'success' && rows.length === 0 && hasFilter && (
            <tr className="empty-row">
              <td colSpan={8}>No accounts match your filters</td>
            </tr>
          )}
          {status === 'success' && rows.map((a) => <AccountsRowView key={a.id} account={a} />)}
        </tbody>
      </table>
    </div>
  )
}

function AccountsRowView({ account: a }: { account: AccountsRow }) {
  const onClick = () => {
    const open = (window as unknown as { openAccountDrawer?: (id: string) => void })
      .openAccountDrawer
    open?.(a.id)
  }

  const icpClass =
    a.icp_score == null
      ? null
      : a.icp_score >= 70
        ? 'score-high'
        : a.icp_score >= 40
          ? 'score-mid'
          : 'score-low'

  return (
    <tr onClick={onClick} style={{ cursor: 'pointer' }}>
      <td>
        <div className="company-cell">
          <div className="co-logo-placeholder">{(a.company_name || '?')[0]}</div>
          <div className="contact-name">{a.company_name || '—'}</div>
        </div>
      </td>
      <td className="td-mono">{a.company_domain || '—'}</td>
      <td className="td-mono">{a.industry || '—'}</td>
      <td className="td-mono">{a.hq_country || '—'}</td>
      <td className="td-mono">{a.employee_count || '—'}</td>
      <td className="td-mono">{a.funding_stage || '—'}</td>
      <td>{icpClass ? <span className={`score-pill ${icpClass}`}>{a.icp_score}</span> : '—'}</td>
      <td className="td-mono">
        {a.contacts_count}
        <span className="phone-only"> {a.contacts_count === 1 ? 'person' : 'people'}</span>
      </td>
    </tr>
  )
}
